import type { UseFetchOptions } from 'nuxt/app'
import type { ISbLink, ISbStoriesParams, ISbStoryParams } from 'storyblok-js-client'
import StoryblokClient from 'storyblok-js-client'

export function useStoryblokApiV2() {
  const nuxt = useNuxtApp()
  const route = useRoute()
  const client = createStoryblokClient()
  const version = route.query._storyblok ? 'draft' : nuxt.$config.public.storyblokVersion
  return {
    client,
    getStory: async <T>(params: ISbStoryParams & { by_slug?: string; by_full_slug?: string; by_uuid?: string }, fetchOptions: UseFetchOptions<any> = {}) => {
      const res = await useFetch('/api/storyblok/v2/story', { query: { params, version }, ...fetchOptions })
      if (res.error.value)
        showError(res.error.value)
      return res.data.value as T
    },
    getStories: async <T>(params?: Omit<ISbStoriesParams, 'by_slugs'> & { by_slugs?: string | string[]; by_full_slugs?: string | string[] }, fetchOptions: UseFetchOptions<any> = {}) => {
      const res = await useFetch('/api/storyblok/v2/stories', { query: { params, version }, ...fetchOptions })
      if (res.error.value)
        showError(res.error.value)
      return res.data.value as { stories: T[]; total: number }
    },
    getPage: async (path?: string, params: ISbStoriesParams & { throwError?: boolean } = {}) => {
      const res = await useFetch('/api/storyblok/v2/page', { query: { path: path || route.path, params, version } })
      if (res.error.value) {
        if (params.throwError) throw res.error.value
        showError(res.error.value as any)
      }
      if (res.data.value?.redirect) {
        if (res.data.value?.redirect.status.toString().startsWith('3'))
          nuxt.runWithContext(() => navigateTo(useTrailingSlash(res.data.value?.redirect.location), { redirectCode: res.data.value?.redirect.status, external: true }))
        showError({ status: res.data.value?.redirect.status })
      }
      return res.data.value
    },
    getForm: async (path: string, params: ISbStoriesParams = {}) => {
      const res = await useFetch('/api/storyblok/v2/form', { query: { path, params, version } })
      if (res.error.value)
        throw res.error.value
      return res.data.value
    },
    getLinks: async (path: string, params: ISbStoriesParams, all?: boolean) => {
      const res = await useFetch('/api/storyblok/v2/links', { query: { path, params, version, all } })
      if (res?.error?.value)
        throw res.error.value
      return res.data.value as { links: ISbLink[]; total: number }
    },
  }
}

export function createStoryblokClient(version: 'draft' | 'published' = 'published') {
  const config = useRuntimeConfig()
  const portal = config.public.storyblokPortal
  const storyblokClient = new StoryblokClient({
    accessToken: version === 'draft' ? config.storyblokPreviewToken : config.storyblokToken,
    cache: {
      clear: 'auto',
      type: 'none',
    },
  })
  return {
    storyblokClient,
    portal,
    getStory: async (path: string, params?: ISbStoriesParams) => {
      const config = useRuntimeConfig()
      return await storyblokClient.getStory(`${portal}/${path}`, {
        version: version || config.public.storyblokVersion as any,
        cv: Date.now(),
        ...params,
      }).then(async (res) => {
        return res.data.story
      }).catch(async (sbError) => {
        return createError({ status: sbError.status })
      })
    },
    getStories: async (params: ISbStoriesParams) => {
      const config = useRuntimeConfig()
      return await storyblokClient.getStories({
        version: version || config.public.storyblokVersion as any,
        cv: Date.now(),
        ...params,
      }).then(async (res) => {
        return res.data.stories
      }).catch(async (sbError) => {
        return createError({ status: sbError.status })
      })
    },
  }
}
